import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Receipt, Search } from "lucide-react";
import { getReceipts, getStats } from "../../services/dataService";
import { DataTable } from "../DataTable";
import { PageHeader, StatCard } from "../PageHeader";
import { StatusBadge } from "../StatusBadge";
import { Input } from "../ui/input";
import { formatCurrency, formatDate } from "../../lib/format";
import { Loader2 } from "lucide-react";

export function ReceiptTracker() {
  const [q, setQ] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: ["receipts"], queryFn: getReceipts });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: getStats });

  const filtered = data.filter(
    (r) =>
      r.client_name.toLowerCase().includes(q.toLowerCase()) ||
      r.reference.toLowerCase().includes(q.toLowerCase())
  );

  const columns = [
    { key: "run_date", header: "Date", sortable: true, accessor: (r) => new Date(r.run_date).getTime(), render: (r) => formatDate(r.run_date) },
    { key: "client_name", header: "Client Name", sortable: true },
    { key: "reference", header: "Reference", mono: true },
    { key: "batch_name", header: "Batch", render: (r) => <span className="text-slate-500">{r.batch_name}</span> },
    { key: "netcash_ref", header: "Netcash Ref", mono: true, render: (r) => <span className="text-slate-500">{r.netcash_ref}</span> },
    { key: "amount", header: "Amount", sortable: true, align: "right", mono: true, render: (r) => formatCurrency(r.amount) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div>
      <PageHeader title="Receipt Tracker" icon={Receipt} description="Historical log of all debit orders run since inception. Sort by date, client name, or amount." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Debit Orders" value={stats?.processed_orders ?? "—"} accent="slate" />
        <StatCard label="Cleared" value={data.filter((r) => r.status === "Processed & Cleared").length} accent="emerald" />
        <StatCard label="In Transit" value={data.filter((r) => r.status === "Submitted to Netcash").length} accent="blue" />
        <StatCard label="Total Value" value={formatCurrency(data.reduce((s, r) => s + r.amount, 0))} accent="slate" />
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client or reference…" className="pl-9" data-testid="receipt-search-input" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : (
        <DataTable columns={columns} data={filtered} initialSort={{ key: "run_date", dir: "desc" }} emptyText="No debit orders recorded yet." testId="receipts-table" />
      )}
    </div>
  );
}
